import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Container,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Close, Schedule } from "@mui/icons-material";
import useResponsive from "../../hooks/useResponsive";
import { CONTACT_INFO } from "../../constants";

// Clave única para localStorage - cambiar si se quiere resetear para todos los usuarios
const BANNER_STORAGE_KEY = "notification_banner_dismissed_v2_horarios_2025";

/**
 * NotificationBanner - Banner horizontal de notificación
 *
 * Muestra un banner en la parte superior de la página con información importante.
 * El usuario puede cerrarlo y elegir no volver a verlo.
 *
 * MEJORAS MOBILE:
 * - Diseño más compacto en una sola línea
 * - Texto abreviado para mobile
 * - Checkbox y botón cerrar optimizados
 */
const NotificationBanner = () => {
  const [visible, setVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { isMobile } = useResponsive();

  // Verificar si el usuario ya cerró el banner permanentemente
  useEffect(() => {
    const isDismissed = localStorage.getItem(BANNER_STORAGE_KEY);
    if (!isDismissed) {
      setVisible(true);
    }
  }, []);

  // Manejar cambio del checkbox - guarda/elimina inmediatamente en localStorage
  const handleDontShowAgainChange = (e) => {
    const checked = e.target.checked;
    setDontShowAgain(checked);

    if (checked) {
      // Guardar inmediatamente en localStorage
      localStorage.setItem(BANNER_STORAGE_KEY, "true");
    } else {
      // Eliminar del localStorage si desmarca
      localStorage.removeItem(BANNER_STORAGE_KEY);
    }
  };

  const handleClose = () => {
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Box
      sx={{
        backgroundColor: "#d32f2f",
        color: "white",
        py: { xs: 2, sm: 2 },
        // Banner pegado justo debajo del navbar fijo. Usa `sticky` para que
        // siga al usuario al hacer scroll y se mantenga adherido bajo la
        // AppBar (que vive en zIndex 1100).
        position: "sticky",
        top: { xs: 56, sm: 64 },
        zIndex: 1099,
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 5, sm: 20 },
            position: "relative",
            pr: { xs: 3, sm: 4, md: 5 },
          }}
        >
          {/* <Schedule sx={{ fontSize: { xs: 14, sm: 20 }, flexShrink: 0 }} /> */}

          <Typography
            variant="body2"
            sx={{
              fontWeight: "medium",
              textAlign: "center",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
              lineHeight: 1.2,
            }}
          >
            {isMobile ? (
              // Versión compacta para mobile
              <>
                <Box component="p" sx={{ fontWeight: "bold" }}>
                  Nuevos Horarios:
                </Box>
                <Box component="p">{CONTACT_INFO.schedule.weekdays}</Box>
                {CONTACT_INFO.schedule.friday}
              </>
            ) : (
              // Versión completa para desktop
              <>
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  📢 Nuevos Horarios:
                </Box>{" "}
                {CONTACT_INFO.schedule.weekdays} |{" "}
                {CONTACT_INFO.schedule.friday}
              </>
            )}
          </Typography>

          {/* Checkbox para no volver a mostrar */}
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={handleDontShowAgainChange}
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  "&.Mui-checked": {
                    color: "white",
                  },
                  p: { xs: 0.25, sm: 0.5 },
                }}
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  whiteSpace: "nowrap",
                }}
              >
                No mostrar más
              </Typography>
            }
            sx={{
              ml: { xs: 0, sm: 1 },
              mr: 0,
              minWidth: "auto",
              "& .MuiFormControlLabel-label": {
                color: "rgba(255,255,255,0.9)",
              },
            }}
          />

          {/* Botón de cerrar */}
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              position: "absolute",
              right: { xs: -4, sm: 0 },
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              padding: { xs: 0.25, sm: 0.5 },
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
              },
            }}
            aria-label="Cerrar notificación"
          >
            <Close sx={{ fontSize: { xs: 16, sm: 20 } }} />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
};

export default NotificationBanner;
